import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../../auth/AuthContext';
import { db, functions } from '../../../../firebase-client';
import { isOutingPast } from '../../../../utils/outingTiming';
import { getRsvpYesMemberIdsForOccurrence, toRsvpRowFromSnapshot } from '../../../../utils/rsvpResolver';
import SubmitButton from '../../formComponents/SubmitButton';
import ErrorMessage from '../../formComponents/ErrorMessage';

const ACTIVE_BOOKING_STATUSES = new Set(['reserved', 'allocated', 'confirmed', 'active']);

function emptyCrewMember() {
  return {
    id: crypto.randomUUID(),
    memberId: '',
    displayName: '',
    seatLabel: '',
  };
}

function emptyCrew() {
  return {
    id: crypto.randomUUID(),
    label: '',
    notes: '',
    members: [],
    preferences: [],
    status: 'draft',
    selectedBoatId: '',
    bookingId: '',
  };
}

function cloneCrew(crew = {}) {
  return {
    id: crypto.randomUUID(),
    label: crew.label || '',
    notes: crew.notes || '',
    members: (Array.isArray(crew.members) ? crew.members : []).map((member) => ({
      ...member,
      id: crypto.randomUUID(),
    })),
    preferences: Array.isArray(crew.preferences) ? [...crew.preferences] : [],
    status: 'draft',
    selectedBoatId: '',
    bookingId: '',
  };
}

function serializePresetCrew(crew = {}) {
  return {
    label: crew.label || '',
    notes: crew.notes || '',
    members: (Array.isArray(crew.members) ? crew.members : []).map((member) => ({
      memberId: member.memberId || '',
      displayName: member.displayName || '',
      seatLabel: member.seatLabel || '',
    })),
    preferences: Array.isArray(crew.preferences) ? [...crew.preferences] : [],
  };
}

function buildCrewFromPreset(crew = {}) {
  return cloneCrew({
    ...crew,
    members: (Array.isArray(crew.members) ? crew.members : []).map((member) => ({
      ...emptyCrewMember(),
      memberId: member.memberId || '',
      displayName: member.displayName || '',
      seatLabel: member.seatLabel || '',
    })),
  });
}

function extractPresetCrew(preset = {}) {
  if (preset?.crew && typeof preset.crew === 'object') return preset.crew;
  if (Array.isArray(preset?.crews) && preset.crews.length > 0) return preset.crews[0];
  return null;
}

function emptyPresetDraft() {
  return {
    label: '',
    notes: '',
    members: [],
    preferences: [],
  };
}

function emptyOuting() {
  return {
    id: null,
    name: '',
    linkedEventId: '',
    linkedEventName: '',
    status: 'draft',
    tagFilters: [],
    date: null,
    dateIso: '',
    fromTime: '',
    toTime: '',
    durationMinutes: 60,
  };
}

function toDateInputValue(dateLike) {
  const date =
    dateLike?.toDate?.() ?? (dateLike instanceof Date ? dateLike : dateLike ? new Date(dateLike) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const WEEKDAY_LABELS = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

function parseDateIsoLocal(dateIso) {
  if (typeof dateIso !== 'string' || dateIso.trim() === '') return null;
  const [year, month, day] = dateIso.trim().split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfLocalDay(value = new Date()) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function hhmmToMinutes(value) {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function timeOverlaps(aFrom, aTo, bFrom, bTo) {
  const aFromMins = hhmmToMinutes(aFrom);
  const aToMins = hhmmToMinutes(aTo);
  const bFromMins = hhmmToMinutes(bFrom);
  const bToMins = hhmmToMinutes(bTo);
  if (
    aFromMins == null ||
    aToMins == null ||
    bFromMins == null ||
    bToMins == null
  ) {
    return true;
  }
  return !(aToMins <= bFromMins || bToMins <= aFromMins);
}

function toOccurrenceDateIso(dateLike) {
  const date = dateLike instanceof Date ? dateLike : parseDateIsoLocal(dateLike);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatOccurrenceLabel(dateIso, timeText = '') {
  const date = parseDateIsoLocal(dateIso);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return dateIso || 'Date TBC';
  const dayLabel = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return timeText ? `${dayLabel} • ${timeText}` : dayLabel;
}

function nthWeekdayOfMonth(year, monthIndex, dayCode, weekOfMonth) {
  const jsDay = DAY_CODES.indexOf(dayCode);
  if (jsDay < 0) return null;
  const targetMonth = new Date(year, monthIndex, 1);
  const offset = (7 + jsDay - targetMonth.getDay()) % 7;
  const firstMatch = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1 + offset);

  if (String(weekOfMonth || '').toLowerCase() === 'last') {
    const lastOfMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
    const backOffset = (7 + lastOfMonth.getDay() - jsDay) % 7;
    const candidate = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), lastOfMonth.getDate() - backOffset);
    return candidate.getMonth() === targetMonth.getMonth() && candidate.getFullYear() === targetMonth.getFullYear()
      ? candidate
      : null;
  }

  const indexMap = {
    first: 0,
    second: 1,
    third: 2,
    fourth: 3,
  };
  const occurrenceIndex = indexMap[String(weekOfMonth || 'First').toLowerCase()] ?? 0;
  const candidate = new Date(firstMatch);
  candidate.setDate(firstMatch.getDate() + occurrenceIndex * 7);
  return candidate.getMonth() === targetMonth.getMonth() && candidate.getFullYear() === targetMonth.getFullYear()
    ? candidate
    : null;
}

function buildRecurringOccurrenceOptions(eventData = {}, selectedDateIso = '') {
  const recurrence = eventData?.recurrence || {};
  const frequency = String(recurrence?.frequency || eventData?.frequency || 'WEEKLY').toUpperCase();
  const timeText =
    typeof recurrence?.time === 'string' && recurrence.time.trim() !== ''
      ? recurrence.time.trim()
      : typeof eventData?.time === 'string'
        ? eventData.time.trim()
        : '';
  const byDayCodes = Array.from(
    new Set(
      (Array.isArray(recurrence?.byDay) ? recurrence.byDay : [])
        .filter((dayCode) => DAY_CODES.includes(dayCode))
    )
  );
  const endDate = parseDateIsoLocal(recurrence?.endDate || '');
  const seriesStart = parseDateIsoLocal(eventData?.date || toDateInputValue(eventData?.start) || '');
  const today = startOfLocalDay();
  const scanStart = seriesStart && seriesStart > today ? seriesStart : today;
  const options = [];
  const seen = new Set();

  const pushDate = (dateValue) => {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return;
    const normalized = startOfLocalDay(dateValue);
    if (seriesStart && normalized < seriesStart) return;
    if (endDate && normalized > endDate) return;
    const value = toOccurrenceDateIso(normalized);
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({
      value,
      label: formatOccurrenceLabel(value, timeText),
    });
  };

  if (frequency === 'MONTHLY') {
    const targetDay = byDayCodes[0] || 'WE';
    const weekOfMonth = recurrence?.weekOfMonth || eventData?.weekOfMonth || 'First';
    const monthCursor = new Date(scanStart.getFullYear(), scanStart.getMonth(), 1);
    for (let monthOffset = 0; monthOffset < 18 && options.length < 12; monthOffset += 1) {
      const candidate = nthWeekdayOfMonth(
        monthCursor.getFullYear(),
        monthCursor.getMonth() + monthOffset,
        targetDay,
        weekOfMonth
      );
      if (candidate && candidate >= scanStart) pushDate(candidate);
    }
  } else {
    const allowedDays = byDayCodes.length ? byDayCodes : ['WE'];
    const cursor = new Date(scanStart);
    for (let dayOffset = 0; dayOffset < 180 && options.length < 12; dayOffset += 1) {
      const candidate = new Date(cursor);
      candidate.setDate(cursor.getDate() + dayOffset);
      if (allowedDays.includes(DAY_CODES[candidate.getDay()])) pushDate(candidate);
    }
  }

  const selectedDate = parseDateIsoLocal(selectedDateIso);
  if (selectedDate && !seen.has(selectedDateIso)) {
    pushDate(selectedDate);
    options.sort((left, right) => left.value.localeCompare(right.value));
  }

  return options;
}

function filterRecurringOccurrenceOptions(options = [], usedDates = new Set(), selectedDateIso = '') {
  return (Array.isArray(options) ? options : []).filter((option) => {
    const value = typeof option?.value === 'string' ? option.value.trim() : '';
    if (!value) return false;
    if (value === selectedDateIso) return true;
    return !usedDates.has(value);
  });
}

function pickMemberName(memberId, memberData = {}) {
  const displayName =
    typeof memberData?.displayName === 'string' ? memberData.displayName.trim() : '';
  if (displayName) return displayName;
  const username =
    typeof memberData?.username === 'string' ? memberData.username.trim() : '';
  if (username) return username;
  const firstName =
    typeof memberData?.firstName === 'string' ? memberData.firstName.trim() : '';
  const lastName =
    typeof memberData?.lastName === 'string' ? memberData.lastName.trim() : '';
  const full = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (typeof memberData?.email === 'string' && memberData.email.trim() !== '') return memberData.email.trim();
  return 'Unknown member';
}

function normalizeTagSet(rawTags) {
  if (Array.isArray(rawTags)) {
    return new Set(
      rawTags
        .filter((tag) => typeof tag === 'string')
        .map((tag) => tag.trim())
        .filter(Boolean)
    );
  }
  if (rawTags && typeof rawTags === 'object') {
    return new Set(
      Object.entries(rawTags)
        .filter(([tagName, enabled]) => Boolean(enabled) && typeof tagName === 'string')
        .map(([tagName]) => tagName.trim())
        .filter(Boolean)
    );
  }
  return new Set();
}

function normalizeCrews(rawCrews = []) {
  return (Array.isArray(rawCrews) ? rawCrews : []).map((crew) => ({
    id: typeof crew?.id === 'string' && crew.id.trim() !== '' ? crew.id.trim() : crypto.randomUUID(),
    label: typeof crew?.label === 'string' ? crew.label : '',
    notes: typeof crew?.notes === 'string' ? crew.notes : '',
    members: (Array.isArray(crew?.members) ? crew.members : [])
      .map((member) => {
        const memberId =
          typeof member?.memberId === 'string' && member.memberId.trim() !== ''
            ? member.memberId.trim()
            : '';
        if (!memberId) return null;
        return {
          id: typeof member?.id === 'string' && member.id.trim() !== '' ? member.id.trim() : crypto.randomUUID(),
          memberId,
          displayName: typeof member?.displayName === 'string' ? member.displayName : '',
          seatLabel: typeof member?.seatLabel === 'string' ? member.seatLabel : '',
        };
      })
      .filter(Boolean),
    preferences: Array.from(
      new Set(
        (Array.isArray(crew?.preferences) ? crew.preferences : [])
          .filter((boatId) => typeof boatId === 'string')
          .map((boatId) => boatId.trim())
          .filter(Boolean)
      )
    ),
    status: typeof crew?.status === 'string' ? crew.status : 'draft',
    selectedBoatId: typeof crew?.selectedBoatId === 'string' ? crew.selectedBoatId : '',
    bookingId: typeof crew?.bookingId === 'string' ? crew.bookingId : '',
  }));
}

function outingSort(left, right) {
  const leftTime = left?.date?.toDate?.()?.getTime?.() ?? 0;
  const rightTime = right?.date?.toDate?.()?.getTime?.() ?? 0;
  if (rightTime !== leftTime) return rightTime - leftTime;
  const leftUpdated = left?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  const rightUpdated = right?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  return rightUpdated - leftUpdated;
}

function outingSortForDirection(left, right, direction = 'desc') {
  const leftTime = left?.date?.toDate?.()?.getTime?.() ?? 0;
  const rightTime = right?.date?.toDate?.()?.getTime?.() ?? 0;
  if (leftTime !== rightTime) {
    return direction === 'asc' ? leftTime - rightTime : rightTime - leftTime;
  }
  const leftUpdated = left?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  const rightUpdated = right?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  return rightUpdated - leftUpdated;
}

function deriveScheduleFromEvent(eventData = {}) {
  const dateIso =
    typeof eventData?.date === 'string' && eventData.date.trim() !== ''
      ? eventData.date.trim()
      : toDateInputValue(eventData?.start);
  const fromTime =
    typeof eventData?.time === 'string' && eventData.time.trim() !== ''
      ? eventData.time.trim()
      : typeof eventData?.recurrence?.time === 'string' && eventData.recurrence.time.trim() !== ''
        ? eventData.recurrence.time.trim()
      : '';
  const durationMinutes = Math.max(1, Number(eventData?.durationMinutes || 60));
  if (!fromTime) {
    return {
      date: null,
      dateIso: dateIso || '',
      fromTime: '',
      toTime: '',
      durationMinutes,
    };
  }
  const [hours, minutes] = fromTime.split(':').map(Number);
  const startMinutes = (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
  const endMinutes = startMinutes + durationMinutes;
  const toTime = `${String(Math.floor((endMinutes % (24 * 60)) / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
  return {
    date: dateIso ? Timestamp.fromDate(new Date(`${dateIso}T00:00:00`)) : null,
    dateIso: dateIso || '',
    fromTime,
    toTime,
    durationMinutes,
  };
}

function resolveOutingSchedule(eventData = {}, occurrenceDateIso = '', allEvents = []) {
  if (!eventData || typeof eventData !== 'object') {
    return deriveScheduleFromEvent(eventData);
  }

  if (!isRecurringEvent(eventData)) {
    return deriveScheduleFromEvent(eventData);
  }

  const targetDateIso =
    typeof occurrenceDateIso === 'string' && occurrenceDateIso.trim() !== ''
      ? occurrenceDateIso.trim()
      : '';
  const occurrenceOverride = targetDateIso
    ? (Array.isArray(allEvents) ? allEvents : []).find((candidate) => (
        candidate?.originalEventId === eventData.id &&
        candidate?.occurrenceId === targetDateIso
      )) || null
    : null;

  const scheduleSource = occurrenceOverride || eventData;
  const baseSchedule = deriveScheduleFromEvent(scheduleSource);
  if (!targetDateIso) return baseSchedule;
  return {
    ...baseSchedule,
    dateIso: targetDateIso,
    date: Timestamp.fromDate(new Date(`${targetDateIso}T00:00:00`)),
  };
}

function isRecurringEvent(eventData = {}) {
  if (String(eventData?.type || '').toLowerCase() === 'recurring') return true;
  return Array.isArray(eventData?.recurrence?.byDay) && eventData.recurrence.byDay.length > 0;
}

function formatEventPickerLabel(eventData = {}, availableOccurrences = null) {
  const name =
    typeof eventData?.name === 'string' && eventData.name.trim() !== '' ? eventData.name.trim() : 'Event';
  if (isRecurringEvent(eventData)) {
    const recurrence = eventData?.recurrence || {};
    const frequency = String(recurrence?.frequency || eventData?.frequency || 'weekly').toLowerCase();
    const days = (Array.isArray(recurrence?.byDay) ? recurrence.byDay : [])
      .map((code) => WEEKDAY_LABELS[code] || code)
      .filter(Boolean)
      .join(', ');
    const timeText =
      typeof recurrence?.time === 'string' && recurrence.time.trim() !== ''
        ? recurrence.time.trim()
        : typeof eventData?.time === 'string'
          ? eventData.time.trim()
          : '';
    const availabilityText =
      Number.isFinite(availableOccurrences) && availableOccurrences >= 0
        ? ` • ${availableOccurrences} upcoming slot${availableOccurrences === 1 ? '' : 's'}`
        : '';
    return `${name} • Recurring ${frequency}${days ? ` • ${days}` : ''}${timeText ? ` • ${timeText}` : ''}${availabilityText}`;
  }
  return `${name} • One-off${eventData?.date ? ` • ${eventData.date}` : ''}${eventData?.time ? ` • ${eventData.time}` : ''}`;
}

function formatOutingDate(outing) {
  if (!outing?.date) return 'Date TBC';
  const date = outing.date?.toDate?.() ?? (outing.date ? new Date(outing.date) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Date TBC';
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function summaryLabel(count, noun) {
  const safeCount = Number(count || 0);
  return `${safeCount} ${noun}${safeCount === 1 ? '' : 's'}`;
}

function getBoatSeatCount(boat = {}) {
  const seats = Number(boat?.seats);
  return Number.isFinite(seats) && seats > 0 ? Math.floor(seats) : null;
}

function boatPreferenceMeta(boat = {}, crew = {}, membersById = new Map()) {
  const seatCount = getBoatSeatCount(boat);
  const crewSize = Array.isArray(crew?.members) ? crew.members.length : 0;
  const requiredTags = Array.isArray(boat?.tagsAllowed)
    ? boat.tagsAllowed.filter((tag) => typeof tag === 'string' && tag.trim() !== '')
    : [];
  const tagCompatible =
    requiredTags.length === 0 ||
    (Array.isArray(crew?.members) ? crew.members : []).every((member) => {
      const memberTags = membersById.get(member.memberId)?.tagsSet || new Set();
      return requiredTags.some((tagName) => memberTags.has(tagName));
    });
  const fitsSeatCount = seatCount == null || crewSize <= seatCount;
  const spareSeats = seatCount == null ? 999 : seatCount - crewSize;
  return {
    seatCount,
    crewSize,
    tagCompatible,
    fitsSeatCount,
    recommended: fitsSeatCount && tagCompatible,
    spareSeats,
  };
}

function crewCanBookBoat(boat = {}, crew = {}, membersById = new Map(), availableBoatIds = null) {
  if (!boat?.id) return false;
  if (String(boat?.status || 'active').toLowerCase() === 'maintenance') return false;
  if (availableBoatIds instanceof Set && !availableBoatIds.has(boat.id)) return false;
  const meta = boatPreferenceMeta(boat, crew, membersById);
  return meta.fitsSeatCount !== false && meta.tagCompatible !== false;
}

function boatMetaLabel(boat = {}, meta = {}) {
  const parts = [];
  if (boat?.type) parts.push(boat.type);
  if (meta?.seatCount != null) parts.push(`${meta.seatCount} seats`);
  if (meta?.recommended && meta?.seatCount != null) {
    parts.push(meta.spareSeats === 0 ? 'exact fit' : `${meta.spareSeats} spare`);
  } else if (meta?.fitsSeatCount === false) {
    parts.push('too small');
  }
  if (meta?.tagCompatible === false) {
    parts.push('tag mismatch');
  }
  return parts.join(' • ');
}

function boatMetaWithAvailabilityLabel(boat = {}, meta = {}) {
  const baseLabel = boatMetaLabel(boat, meta);
  const parts = baseLabel ? [baseLabel] : [];
  if (meta?.availableNow === false) parts.push('taken');
  return parts.join(' • ');
}

export default function EditOutingsForm({
  isOpen,
  onClose,
  onSave,
  teamId,
  teamTags = [],
  focusOutingId = null,
  createNew = false,
  focusPresets = false,
}) {
  const { user } = useAuth();
  const presetsRef = useRef(null);
  const modalScrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [outings, setOutings] = useState([]);
  const [activeKey, setActiveKey] = useState('__new__');
  const [outing, setOuting] = useState(emptyOuting());
  const [crews, setCrews] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [boats, setBoats] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [rsvpYesIds, setRsvpYesIds] = useState([]);
  const [rsvpSeriesResponsesEnabled, setRsvpSeriesResponsesEnabled] = useState(true);
  const [memberPickerByCrew, setMemberPickerByCrew] = useState({});
  const [memberSearchByCrew, setMemberSearchByCrew] = useState({});
  const [collapsedCrews, setCollapsedCrews] = useState({});
  const [memberChooser, setMemberChooser] = useState(null);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetPickerByCrew, setPresetPickerByCrew] = useState({});
  const [presetManagerName, setPresetManagerName] = useState('');
  const [presetDraft, setPresetDraft] = useState(emptyPresetDraft());
  const [presetMemberSearch, setPresetMemberSearch] = useState('');
  const [presetMemberPicker, setPresetMemberPicker] = useState('');
  const [presetBoatSearch, setPresetBoatSearch] = useState('');
  const [showAllPresetBoats, setShowAllPresetBoats] = useState(false);
  const [notice, setNotice] = useState('');
  const [highlightPresets, setHighlightPresets] = useState(false);
  const [availableBoatIds, setAvailableBoatIds] = useState(null);
  const [checkingBoatAvailability, setCheckingBoatAvailability] = useState(false);
  const [boatAvailabilityError, setBoatAvailabilityError] = useState('');
  const [outingListFilter, setOutingListFilter] = useState('upcoming');
  const [showOutingsHelp, setShowOutingsHelp] = useState(false);
  const [isMobileOutingsView, setIsMobileOutingsView] = useState(false);
  const [mobileStep, setMobileStep] = useState('outings');

  const submitOutingFn = useMemo(() => httpsCallable(functions, 'submitSailingOuting'), []);
  const reopenOutingDraftFn = useMemo(() => httpsCallable(functions, 'reopenSailingOutingDraft'), []);
  const presetManagementOnly = focusPresets && !focusOutingId && !createNew;

  const teamTagNames = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(teamTags) ? teamTags : [])
            .filter((tag) => tag?.type !== 'ageRule' && typeof tag?.name === 'string')
            .map((tag) => tag.name.trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' })),
    [teamTags]
  );

  const membersById = useMemo(() => {
    const map = new Map();
    memberOptions.forEach((member) => map.set(member.id, member));
    return map;
  }, [memberOptions]);
  const boatsById = useMemo(() => {
    const map = new Map();
    boats.forEach((boat) => {
      if (boat?.id) map.set(boat.id, boat);
    });
    return map;
  }, [boats]);

  const outingsById = useMemo(() => {
    const map = new Map();
    outings.forEach((row) => map.set(row.id, row));
    return map;
  }, [outings]);
  const eventOptionsById = useMemo(() => {
    const map = new Map();
    eventOptions.forEach((eventData) => map.set(eventData.id, eventData));
    return map;
  }, [eventOptions]);

  const activeOutingMeta = activeKey !== '__new__' ? outingsById.get(activeKey) || null : null;
  const isSubmitted = String(outing?.status || '').toLowerCase() === 'submitted';
  const activeOutingIsPast = isOutingPast(outing);
  const selectedPreset = useMemo(
    () => presets.find((preset) => preset.id === selectedPresetId) || null,
    [presets, selectedPresetId]
  );
  const selectedPresetCrew = useMemo(() => extractPresetCrew(selectedPreset), [selectedPreset]);
  const crewStatusSummary = useMemo(
    () =>
      crews.reduce(
        (acc, crew) => {
          const status = String(crew?.status || 'draft').toLowerCase();
          if (status === 'allocated') acc.allocated += 1;
          else if (status === 'unfilled') acc.unfilled += 1;
          else if (status === 'pending') acc.pending += 1;
          else acc.draft += 1;
          return acc;
        },
        { draft: 0, pending: 0, allocated: 0, unfilled: 0 }
      ),
    [crews]
  );
  const outingsSummary = useMemo(
    () =>
      outings.reduce(
        (acc, row) => {
          const status = String(row?.status || 'draft').toLowerCase();
          if (status === 'submitted') acc.submitted += 1;
          else acc.draft += 1;
          return acc;
        },
        { draft: 0, submitted: 0 }
      ),
    [outings]
  );
  const visibleOutings = useMemo(() => {
    const filtered = outings.filter((row) => {
      if (outingListFilter === 'all') return true;
      const outingIsPast = isOutingPast(row);
      if (outingListFilter === 'past') return outingIsPast;
      return !outingIsPast;
    });
    return [...filtered].sort((left, right) =>
      outingSortForDirection(left, right, outingListFilter === 'past' ? 'desc' : 'asc')
    );
  }, [outingListFilter, outings]);

  const usedOneOffEventIds = useMemo(() => {
    const currentId = activeKey !== '__new__' ? activeKey : null;
    return new Set(
      outings
        .filter((row) => row.id !== currentId)
        .filter((row) => {
          const linkedEventId =
            typeof row?.linkedEventId === 'string' ? row.linkedEventId.trim() : '';
          if (!linkedEventId) return false;
          return !isRecurringEvent(eventOptionsById.get(linkedEventId) || {});
        })
        .map((row) => (typeof row?.linkedEventId === 'string' ? row.linkedEventId.trim() : ''))
        .filter(Boolean)
    );
  }, [activeKey, eventOptionsById, outings]);

  const usedRecurringOccurrencesByEvent = useMemo(() => {
    const currentId = activeKey !== '__new__' ? activeKey : null;
    const map = new Map();
    outings
      .filter((row) => row.id !== currentId)
      .forEach((row) => {
        const linkedEventId =
          typeof row?.linkedEventId === 'string' ? row.linkedEventId.trim() : '';
        const dateIso =
          typeof row?.dateIso === 'string' ? row.dateIso.trim() : toDateInputValue(row?.date);
        if (!linkedEventId || !dateIso) return;
        if (!isRecurringEvent(eventOptionsById.get(linkedEventId) || {})) return;
        const current = map.get(linkedEventId) || new Set();
        current.add(dateIso);
        map.set(linkedEventId, current);
      });
    return map;
  }, [activeKey, eventOptionsById, outings]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [outingsSnap, boatsSnap, membersSnap, eventsSnap, teamSnap] = await Promise.all([
          getDocs(query(collection(db, 'teams', teamId, 'sailingOutings'), orderBy('updatedAt', 'desc'))),
          getDocs(collection(db, 'teams', teamId, 'sailingBoats')),
          getDocs(collection(db, 'teams', teamId, 'members')),
          getDocs(collection(db, 'teams', teamId, 'events')),
          getDoc(doc(db, 'teams', teamId)),
        ]);
        if (cancelled) return;
        setRsvpSeriesResponsesEnabled(teamSnap.exists() ? teamSnap.data()?.eventSettings?.rsvpSeriesResponsesEnabled !== false : true);

        const nextOutings = outingsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort(outingSort);
        setOutings(nextOutings);

        const nextBoats = boatsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((left, right) => (left?.name || '').localeCompare(right?.name || '', undefined, { sensitivity: 'base' }));
        setBoats(nextBoats);

        const userSnaps = await Promise.all(
          membersSnap.docs.map((docSnap) => getDoc(doc(db, 'users', docSnap.id)).catch(() => null))
        );

        const nextMembers = membersSnap.docs
          .map((docSnap, index) => {
            const data = docSnap.data() || {};
            const userData = userSnaps[index]?.exists?.() ? userSnaps[index].data() || {} : {};
            const mergedData = { ...userData, ...data };
            return {
              id: docSnap.id,
              name: pickMemberName(docSnap.id, mergedData),
              tagsSet: normalizeTagSet(mergedData?.tags),
            };
          })
          .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
        setMemberOptions(nextMembers);

        const nextEvents = eventsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((eventData) => {
            if (String(eventData?.attendanceMode || '').toLowerCase() !== 'rsvp') return false;
            const recurring = isRecurringEvent(eventData);
            if (recurring) {
              return Array.isArray(eventData?.recurrence?.byDay) && eventData.recurrence.byDay.length > 0 && typeof eventData?.recurrence?.time === 'string' && eventData.recurrence.time.trim() !== '';
            }
            return Boolean(eventData?.date && eventData?.time);
          })
          .sort((left, right) => {
            const leftKey = `${left?.date || ''} ${left?.time || ''}`;
            const rightKey = `${right?.date || ''} ${right?.time || ''}`;
            return rightKey.localeCompare(leftKey);
          });
        setEventOptions(nextEvents);

        const initialKey = presetManagementOnly
          ? '__new__'
          : createNew
            ? '__new__'
            : focusOutingId && nextOutings.some((row) => row.id === focusOutingId)
              ? focusOutingId
              : nextOutings[0]?.id || '__new__';
        setActiveKey(initialKey);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load outings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [createNew, focusOutingId, isOpen, presetManagementOnly, teamId]);

  useEffect(() => {
    if (!isOpen) return;
    setMemberPickerByCrew({});
    setMemberSearchByCrew({});
    setCollapsedCrews({});
    setPresetPickerByCrew({});
    setMemberChooser(null);
    setNotice('');
  }, [activeKey, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(max-width: 900px)');
    const apply = () => setIsMobileOutingsView(media.matches);
    apply();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    media.addListener(apply);
    return () => media.removeListener(apply);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setMobileStep('outings');
  }, [isOpen]);

  const scrollMobileModal = () => {
    if (!isMobileOutingsView) return;
    const scrollNode = modalScrollRef.current?.closest('.modal-content--outings') || modalScrollRef.current;
    scrollNode?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const goToMobileStep = (nextStep) => {
    setMobileStep(nextStep);
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        scrollMobileModal();
      });
    }
  };

  useEffect(() => {
    if (!isOpen || !focusPresets) return;
    const timer = window.setTimeout(() => {
      presetsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightPresets(true);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusPresets, isOpen]);

  useEffect(() => {
    if (!highlightPresets) return;
    const timer = window.setTimeout(() => setHighlightPresets(false), 1800);
    return () => window.clearTimeout(timer);
  }, [highlightPresets]);

  useEffect(() => {
    if (!isOpen || !teamId || !user?.uid) {
      setPresets([]);
      setSelectedPresetId('');
      return;
    }
    let cancelled = false;
    const loadPresets = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'users', user.uid, 'outingCrewPresets'), orderBy('updatedAt', 'desc')));
        if (cancelled) return;
        const nextPresets = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((preset) => preset.teamId === teamId)
          .filter((preset) => extractPresetCrew(preset));
        setPresets(nextPresets);
        setSelectedPresetId((prev) => (nextPresets.some((preset) => preset.id === prev) ? prev : nextPresets[0]?.id || ''));
      } catch {
        if (!cancelled) {
          setPresets([]);
          setSelectedPresetId('');
        }
      }
    };
    loadPresets();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId, user?.uid]);

  useEffect(() => {
    setPresetManagerName(selectedPreset?.name || '');
    setPresetDraft(
      selectedPresetCrew
        ? buildCrewFromPreset(selectedPresetCrew)
        : emptyPresetDraft()
    );
    setPresetMemberSearch('');
    setPresetMemberPicker('');
    setPresetBoatSearch('');
    setShowAllPresetBoats(false);
  }, [selectedPreset, selectedPresetCrew]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeKey === '__new__') {
      setOuting(emptyOuting());
      setCrews([]);
      return;
    }
    let cancelled = false;
    const loadOuting = async () => {
      const outingMeta = outingsById.get(activeKey);
      if (!outingMeta) return;
      try {
        const crewsSnap = await getDocs(collection(db, 'teams', teamId, 'sailingOutings', activeKey, 'crews'));
        if (cancelled) return;
        setOuting({
          id: activeKey,
          name: typeof outingMeta?.name === 'string' ? outingMeta.name : '',
          linkedEventId: typeof outingMeta?.linkedEventId === 'string' ? outingMeta.linkedEventId : '',
          linkedEventName: typeof outingMeta?.linkedEventName === 'string' ? outingMeta.linkedEventName : '',
          status: typeof outingMeta?.status === 'string' ? outingMeta.status : 'draft',
          tagFilters: Array.isArray(outingMeta?.tagFilters) ? outingMeta.tagFilters : [],
          date: outingMeta?.date || null,
          dateIso: typeof outingMeta?.dateIso === 'string' ? outingMeta.dateIso : toDateInputValue(outingMeta?.date),
          fromTime: typeof outingMeta?.fromTime === 'string' ? outingMeta.fromTime : '',
          toTime: typeof outingMeta?.toTime === 'string' ? outingMeta.toTime : '',
          durationMinutes: Number(outingMeta?.durationMinutes || 60),
        });
        setCrews(
          normalizeCrews(
            crewsSnap.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          )
        );
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load outing details.');
      }
    };
    loadOuting();
    return () => {
      cancelled = true;
    };
  }, [activeKey, isOpen, outingsById, teamId]);

  useEffect(() => {
    if (!isOpen || !outing?.linkedEventId) {
      setRsvpYesIds([]);
      return;
    }
    let cancelled = false;
    const loadRsvps = async () => {
      try {
        const snap = await getDocs(collection(db, 'teams', teamId, 'events', outing.linkedEventId, 'rsvps'));
        if (cancelled) return;
        const linkedEvent = eventOptions.find(eventData => eventData.id === outing.linkedEventId) || {};
        const rows = snap.docs.map(toRsvpRowFromSnapshot).filter(Boolean);
        const ids = getRsvpYesMemberIdsForOccurrence({
          rows,
          occurrenceId: isRecurringEvent(linkedEvent) ? outing.dateIso || null : null,
          isRecurring: isRecurringEvent(linkedEvent),
          seriesResponsesEnabled: rsvpSeriesResponsesEnabled,
        });
        setRsvpYesIds(ids);
      } catch {
        if (!cancelled) setRsvpYesIds([]);
      }
    };
    loadRsvps();
    return () => {
      cancelled = true;
    };
  }, [eventOptions, isOpen, outing?.dateIso, outing?.linkedEventId, rsvpSeriesResponsesEnabled, teamId]);

  const selectedEvent = useMemo(
    () => eventOptions.find((eventData) => eventData.id === outing.linkedEventId) || null,
    [eventOptions, outing.linkedEventId]
  );
  const selectedEventIsRecurring = isRecurringEvent(selectedEvent || {});
  const resolvedSelectedEventSchedule = useMemo(
    () => (selectedEvent
      ? resolveOutingSchedule(selectedEvent, outing.dateIso, eventOptions)
      : deriveScheduleFromEvent(outing)),
    [eventOptions, outing, selectedEvent]
  );

  useEffect(() => {
    if (!selectedEvent) return;
    const nextDateIso = selectedEventIsRecurring
      ? (outing.dateIso || resolvedSelectedEventSchedule.dateIso || '')
      : (resolvedSelectedEventSchedule.dateIso || '');
    const nextDate = nextDateIso
      ? Timestamp.fromDate(new Date(`${nextDateIso}T00:00:00`))
      : resolvedSelectedEventSchedule.date || null;
    setOuting((prev) => {
      if (
        prev.dateIso === nextDateIso &&
        prev.fromTime === (resolvedSelectedEventSchedule.fromTime || '') &&
        prev.toTime === (resolvedSelectedEventSchedule.toTime || '') &&
        Number(prev.durationMinutes || 60) === Number(resolvedSelectedEventSchedule.durationMinutes || 60)
      ) {
        return prev;
      }
      return {
        ...prev,
        dateIso: nextDateIso,
        date: nextDate,
        fromTime: resolvedSelectedEventSchedule.fromTime || '',
        toTime: resolvedSelectedEventSchedule.toTime || '',
        durationMinutes: resolvedSelectedEventSchedule.durationMinutes || 60,
      };
    });
  }, [
    outing.durationMinutes,
    outing.dateIso,
    outing.id,
    outing.linkedEventId,
    outing.name,
    resolvedSelectedEventSchedule.date,
    resolvedSelectedEventSchedule.dateIso,
    resolvedSelectedEventSchedule.durationMinutes,
    resolvedSelectedEventSchedule.fromTime,
    resolvedSelectedEventSchedule.toTime,
    selectedEvent,
    selectedEventIsRecurring,
  ]);

  useEffect(() => {
    if (!isOpen || !teamId || !outing?.dateIso || !outing?.fromTime || !outing?.toTime) {
      setAvailableBoatIds(null);
      setCheckingBoatAvailability(false);
      setBoatAvailabilityError('');
      return undefined;
    }

    const activeBoats = boats.filter(
      (boat) => String(boat?.status || 'active').toLowerCase() !== 'maintenance'
    );
    if (!activeBoats.length) {
      setAvailableBoatIds(new Set());
      setCheckingBoatAvailability(false);
      setBoatAvailabilityError('');
      return undefined;
    }

    let cancelled = false;
    setAvailableBoatIds(new Set());
    setCheckingBoatAvailability(true);
    setBoatAvailabilityError('');

    const outingDayStart = parseDateIsoLocal(outing.dateIso);
    if (!(outingDayStart instanceof Date) || Number.isNaN(outingDayStart.getTime())) {
      setAvailableBoatIds(null);
      setCheckingBoatAvailability(false);
      return undefined;
    }

    outingDayStart.setHours(0, 0, 0, 0);
    const outingDayEnd = new Date(outingDayStart);
    outingDayEnd.setDate(outingDayEnd.getDate() + 1);

    (async () => {
      try {
        const dayStartTs = Timestamp.fromDate(outingDayStart);
        const dayEndTs = Timestamp.fromDate(outingDayEnd);
        const checks = await Promise.all(
          activeBoats.map(async (boat) => {
            const bookingsQuery = query(
              collection(db, 'teams', teamId, 'sailingBoats', boat.id, 'bookings'),
              where('date', '>=', dayStartTs),
              where('date', '<', dayEndTs)
            );
            const bookingsSnap = await getDocs(bookingsQuery);
            const hasClash = bookingsSnap.docs.some((docSnap) => {
              const booking = docSnap.data() || {};
              const status = String(booking?.status || 'reserved').toLowerCase();
              if (!ACTIVE_BOOKING_STATUSES.has(status)) return false;
              return timeOverlaps(
                outing.fromTime,
                outing.toTime,
                booking?.fromTime || null,
                booking?.toTime || null
              );
            });
            return {
              boatId: boat.id,
              available: !hasClash,
            };
          })
        );

        if (cancelled) return;
        setAvailableBoatIds(new Set(checks.filter((entry) => entry.available).map((entry) => entry.boatId)));
      } catch {
        if (cancelled) return;
        setAvailableBoatIds(null);
        setBoatAvailabilityError('Could not check live boat availability. Showing all otherwise-eligible boats.');
      } finally {
        if (!cancelled) setCheckingBoatAvailability(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    boats,
    isOpen,
    outing?.dateIso,
    outing?.durationMinutes,
    outing?.fromTime,
    outing?.id,
    outing?.linkedEventId,
    outing?.name,
    outing?.toTime,
    teamId,
  ]);

  useEffect(() => {
    if (isSubmitted) return;
    setCrews((prev) => {
      let changed = false;
      const nextCrews = prev.map((crew) => {
        const nextPreferences = (Array.isArray(crew?.preferences) ? crew.preferences : []).filter((boatId) => {
          const boat = boatsById.get(boatId);
          return crewCanBookBoat(boat, crew, membersById, availableBoatIds);
        });
        if (nextPreferences.length === crew.preferences.length) return crew;
        changed = true;
        return {
          ...crew,
          preferences: nextPreferences,
        };
      });
      return changed ? nextCrews : prev;
    });
  }, [availableBoatIds, boatsById, crews, isSubmitted, membersById]);
  const usedOccurrenceDatesForSelectedEvent = useMemo(
    () => usedRecurringOccurrencesByEvent.get(outing.linkedEventId) || new Set(),
    [outing.linkedEventId, usedRecurringOccurrencesByEvent]
  );
  const recurringOccurrenceOptions = useMemo(
    () =>
      selectedEventIsRecurring
        ? filterRecurringOccurrenceOptions(
            buildRecurringOccurrenceOptions(selectedEvent || {}, outing.dateIso),
            usedOccurrenceDatesForSelectedEvent,
            outing.dateIso
          )
        : [],
    [outing.dateIso, selectedEvent, selectedEventIsRecurring, usedOccurrenceDatesForSelectedEvent]
  );
  const rsvpYesSet = useMemo(() => new Set(rsvpYesIds), [rsvpYesIds]);

  const eligibleMembers = useMemo(() => {
    return memberOptions.filter((member) => {
      if (!rsvpYesSet.has(member.id)) return false;
      if (!outing.tagFilters.length) return true;
      return outing.tagFilters.some((tagName) => member.tagsSet.has(tagName));
    });
  }, [memberOptions, outing.tagFilters, rsvpYesSet]);

  const eligibleMembersById = useMemo(() => {
    const map = new Map();
    eligibleMembers.forEach((member) => map.set(member.id, member));
    return map;
  }, [eligibleMembers]);

  const crewMembershipMap = useMemo(() => {
    const map = new Map();
    crews.forEach((crew) => {
      crew.members.forEach((member) => {
        map.set(member.memberId, crew.id);
      });
    });
    return map;
  }, [crews]);

  const assignedMemberIds = useMemo(
    () =>
      new Set(
        crews.flatMap((crew) =>
          (Array.isArray(crew?.members) ? crew.members : [])
            .map((member) => (typeof member?.memberId === 'string' ? member.memberId.trim() : ''))
            .filter(Boolean)
        )
      ),
    [crews]
  );

  const unallocatedRsvpMembers = useMemo(
    () =>
      memberOptions.filter(
        (member) =>
          rsvpYesSet.has(member.id) &&
          !assignedMemberIds.has(member.id)
      ),
    [assignedMemberIds, memberOptions, rsvpYesSet]
  );

  const selectedEventOptions = useMemo(
    () =>
      eventOptions.filter((eventData) => {
        if (eventData.id === outing.linkedEventId) return true;
        if (!isRecurringEvent(eventData)) {
          return !usedOneOffEventIds.has(eventData.id);
        }
        const usedDates = usedRecurringOccurrencesByEvent.get(eventData.id) || new Set();
        const availableDates = filterRecurringOccurrenceOptions(
          buildRecurringOccurrenceOptions(eventData),
          usedDates
        );
        return availableDates.length > 0;
      }),
    [eventOptions, outing.linkedEventId, usedOneOffEventIds, usedRecurringOccurrencesByEvent]
  );

  const eventOptionGroups = useMemo(
    () => ({
      recurring: selectedEventOptions.filter((eventData) => isRecurringEvent(eventData)),
      oneOff: selectedEventOptions.filter((eventData) => !isRecurringEvent(eventData)),
    }),
    [selectedEventOptions]
  );

  const hydrateOutingFromEvent = (eventId) => {
    const nextEvent = eventOptions.find((eventData) => eventData.id === eventId) || null;
    if (!nextEvent) return;
    setOuting((prev) => ({
      ...prev,
      ...(function getNextValues() {
        const recurrenceOptions = isRecurringEvent(nextEvent)
          ? filterRecurringOccurrenceOptions(
              buildRecurringOccurrenceOptions(nextEvent, prev.linkedEventId === nextEvent.id ? prev.dateIso : ''),
              usedRecurringOccurrencesByEvent.get(nextEvent.id) || new Set(),
              prev.linkedEventId === nextEvent.id ? prev.dateIso : ''
            )
          : [];
        const nextDateIso = isRecurringEvent(nextEvent)
          ? (recurrenceOptions.find((option) => option.value === prev.dateIso)?.value ||
              recurrenceOptions[0]?.value ||
              '')
          : deriveScheduleFromEvent(nextEvent).dateIso;
        const schedule = resolveOutingSchedule(nextEvent, nextDateIso, eventOptions);
        return {
          linkedEventId: nextEvent.id,
          linkedEventName: nextEvent.name || 'Event',
          name: prev.name || nextEvent.name || '',
          ...schedule,
          dateIso: nextDateIso,
          date: nextDateIso ? Timestamp.fromDate(new Date(`${nextDateIso}T00:00:00`)) : null,
        };
      })(),
    }));
  };

  const refreshOutingsList = async (preferredId = null) => {
    const snap = await getDocs(query(collection(db, 'teams', teamId, 'sailingOutings'), orderBy('updatedAt', 'desc')));
    const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })).sort(outingSort);
    setOutings(rows);
    if (preferredId) setActiveKey(preferredId);
    else if (!rows.length) setActiveKey('__new__');
  };

  const refreshPresets = async (preferredId = '') => {
    if (!teamId || !user?.uid) return;
    const snap = await getDocs(query(collection(db, 'users', user.uid, 'outingCrewPresets'), orderBy('updatedAt', 'desc')));
    const nextPresets = snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((preset) => preset.teamId === teamId)
      .filter((preset) => extractPresetCrew(preset));
    setPresets(nextPresets);
    setSelectedPresetId(preferredId || (nextPresets.some((preset) => preset.id === selectedPresetId) ? selectedPresetId : nextPresets[0]?.id || ''));
  };

  const persistDraft = async () => {
    const eventSchedule = selectedEvent
      ? resolveOutingSchedule(selectedEvent, outing.dateIso, eventOptions)
      : deriveScheduleFromEvent(outing);
    const dateIso = selectedEventIsRecurring ? outing.dateIso : eventSchedule.dateIso;
    const date = dateIso ? Timestamp.fromDate(new Date(`${dateIso}T00:00:00`)) : eventSchedule.date;
    const outingPayload = {
      name: outing.name.trim() || selectedEvent?.name || 'Outing',
      linkedEventId: outing.linkedEventId || '',
      linkedEventName: selectedEvent?.name || outing.linkedEventName || '',
      status: 'draft',
      tagFilters: Array.from(new Set((outing.tagFilters || []).filter(Boolean))),
      date: date || null,
      dateIso: dateIso || '',
      fromTime: eventSchedule.fromTime || '',
      toTime: eventSchedule.toTime || '',
      durationMinutes: eventSchedule.durationMinutes || 60,
      updatedAt: serverTimestamp(),
    };

    let outingId = outing.id;
    if (!outingId) {
      const ref = await addDoc(collection(db, 'teams', teamId, 'sailingOutings'), {
        ...outingPayload,
        createdAt: serverTimestamp(),
      });
      outingId = ref.id;
    } else {
      await updateDoc(doc(db, 'teams', teamId, 'sailingOutings', outingId), outingPayload);
    }

    const existingCrewsSnap = await getDocs(collection(db, 'teams', teamId, 'sailingOutings', outingId, 'crews'));
    const existingIds = new Set(existingCrewsSnap.docs.map((docSnap) => docSnap.id));
    const nextIds = new Set();

    for (const crew of crews) {
      const crewId = crew.id || crypto.randomUUID();
      nextIds.add(crewId);
      const members = crew.members
        .map((member) => {
          const memberData = membersById.get(member.memberId);
          if (!memberData) return null;
          return {
            memberId: member.memberId,
            displayName: member.displayName || memberData.name,
            seatLabel: member.seatLabel || '',
          };
        })
        .filter(Boolean);
      const crewPayload = {
        label: crew.label.trim(),
        notes: crew.notes.trim(),
        members,
        memberIds: members.map((member) => member.memberId),
        preferences: Array.from(new Set((crew.preferences || []).filter(Boolean))),
        status: 'draft',
        updatedAt: serverTimestamp(),
      };
      await setDoc(
        doc(db, 'teams', teamId, 'sailingOutings', outingId, 'crews', crewId),
        existingIds.has(crewId)
          ? crewPayload
          : {
              ...crewPayload,
              createdAt: serverTimestamp(),
            },
        { merge: true }
      );
    }

    await Promise.all(
      existingCrewsSnap.docs
        .filter((docSnap) => !nextIds.has(docSnap.id))
        .map((docSnap) => deleteDoc(docSnap.ref))
    );

    setOuting((prev) => ({ ...prev, id: outingId, status: 'draft', ...outingPayload }));
    await refreshOutingsList(outingId);
    return outingId;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    try {
      const outingId = await persistDraft();
      onSave?.({ outingId });
    } catch (err) {
      setError(err?.message || 'Failed to save the outing draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const outingId = await persistDraft();
      const submitResult = await submitOutingFn({ teamId, outingId });
      const nextMeta = await getDoc(doc(db, 'teams', teamId, 'sailingOutings', outingId));
      const crewsSnap = await getDocs(collection(db, 'teams', teamId, 'sailingOutings', outingId, 'crews'));
      if (nextMeta.exists()) {
        setOuting((prev) => ({ ...prev, id: outingId, ...(nextMeta.data() || {}), status: 'submitted' }));
      }
      setCrews(
        normalizeCrews(
          crewsSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        )
      );
      await refreshOutingsList(outingId);
      const resultData = submitResult?.data || {};
      if (resultData.immediateAllocation) {
        if (Number(resultData.unfilledCount || 0) > 0) {
          setNotice(
            `${Number(resultData.allocatedCount || 0)} crew${Number(resultData.allocatedCount || 0) === 1 ? '' : 's'} allocated immediately, ${Number(resultData.unfilledCount || 0)} unfilled.`
          );
        } else {
          setNotice(
            `${Number(resultData.allocatedCount || 0)} crew${Number(resultData.allocatedCount || 0) === 1 ? '' : 's'} allocated immediately.`
          );
        }
      } else {
        setNotice('Submitted to the shared allocation run.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to submit outing.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!outing.id) return;
    setSaving(true);
    setError('');
    try {
      await reopenOutingDraftFn({ teamId, outingId: outing.id });
      await refreshOutingsList(outing.id);
      setOuting((prev) => ({ ...prev, status: 'draft' }));
      setCrews((prev) =>
        prev.map((crew) => ({
          ...crew,
          status: 'draft',
          selectedBoatId: '',
          bookingId: '',
        }))
      );
    } catch (err) {
      setError(err?.message || 'Failed to reopen outing draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDraftOuting = async (outingId) => {
    const outingRow = outingsById.get(outingId);
    if (!outingId || String(outingRow?.status || 'draft').toLowerCase() !== 'draft') return;
    const confirmed =
      typeof window !== 'undefined'
        ? window.confirm(`Delete draft outing "${outingRow?.name || outingRow?.linkedEventName || 'Outing'}"?`)
        : false;
    if (!confirmed) return;
    setSaving(true);
    setError('');
    try {
      const crewsSnap = await getDocs(collection(db, 'teams', teamId, 'sailingOutings', outingId, 'crews'));
      await Promise.all(crewsSnap.docs.map((docSnap) => deleteDoc(docSnap.ref)));
      await deleteDoc(doc(db, 'teams', teamId, 'sailingOutings', outingId));
      const nextVisibleOutings = visibleOutings.filter((row) => row.id !== outingId);
      const fallbackId = nextVisibleOutings[0]?.id || outings.filter((row) => row.id !== outingId)[0]?.id || '__new__';
      if (activeKey === outingId) {
        setActiveKey(fallbackId);
      }
      await refreshOutingsList(activeKey === outingId ? fallbackId : null);
      if (activeKey === outingId && fallbackId === '__new__') {
        setOuting(emptyOuting());
        setCrews([]);
      }
      setNotice(`Deleted draft outing "${outingRow?.name || outingRow?.linkedEventName || 'Outing'}".`);
    } catch (err) {
      setError(err?.message || 'Failed to delete outing draft.');
    } finally {
      setSaving(false);
    }
  };

  const addCrew = () => {
    const nextCrew = emptyCrew();
    setCrews((prev) => [...prev, nextCrew]);
    setCollapsedCrews((prev) => ({ ...prev, [nextCrew.id]: false }));
  };

  const updateCrew = (crewId, patch) => {
    setCrews((prev) =>
      prev.map((crew) => (crew.id === crewId ? { ...crew, ...patch } : crew))
    );
  };

  const removeCrew = (crewId) => {
    setCrews((prev) => prev.filter((crew) => crew.id !== crewId));
    setCollapsedCrews((prev) => {
      const next = { ...prev };
      delete next[crewId];
      return next;
    });
  };

  const savePresetFromCrew = async (sourceCrew, presetName = '', existingPresetId = '') => {
    if (!teamId || !user?.uid || !sourceCrew) return;
    const nextPresetName =
      typeof presetName === 'string' && presetName.trim() !== ''
        ? presetName.trim()
        : sourceCrew.label || `${outing.name || 'Outing'} crew`;
    if (!nextPresetName) return;
    const payload = {
      teamId,
      name: nextPresetName,
      crew: serializePresetCrew(sourceCrew),
      updatedAt: serverTimestamp(),
    };
    if (existingPresetId) {
      await updateDoc(doc(db, 'users', user.uid, 'outingCrewPresets', existingPresetId), payload);
      await refreshPresets(existingPresetId);
      setNotice(`Updated preset "${nextPresetName}".`);
    } else {
      const ref = await addDoc(collection(db, 'users', user.uid, 'outingCrewPresets'), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      await refreshPresets(ref.id);
      setNotice(`Saved preset "${nextPresetName}".`);
    }
  };

  const runPresetAction = async (action, fallbackMessage = 'Failed to save the preset.') => {
    setSaving(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err?.message || fallbackMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePreset = async () => {
    await runPresetAction(
      () => savePresetFromCrew(presetDraft, presetManagerName),
      'Failed to save the preset.'
    );
  };

  const handleUpdatePreset = async () => {
    if (!selectedPreset?.id) return;
    await runPresetAction(
      () => savePresetFromCrew(presetDraft, presetManagerName, selectedPreset.id),
      'Failed to update the preset.'
    );
  };

  const handleSaveCrewAsPreset = async (sourceCrew) => {
    if (!sourceCrew) return;
    const fallbackName = (sourceCrew.label || '').trim() || `${outing.name || 'Outing'} crew`;
    const presetName =
      typeof window !== 'undefined'
        ? window.prompt('Preset name', fallbackName)
        : fallbackName;
    if (presetName == null) return;
    const nextPresetName = presetName.trim();
    if (!nextPresetName) {
      setError('Add a preset name before saving.');
      return;
    }
    await runPresetAction(
      () => savePresetFromCrew(sourceCrew, nextPresetName),
      'Failed to save the preset.'
    );
  };

  const applyPresetToCrew = (crewId, presetId) => {
    const preset = presets.find((entry) => entry.id === presetId);
    const presetCrew = extractPresetCrew(preset);
    if (!preset || !presetCrew) return;
    const nextCrew = buildCrewFromPreset(presetCrew);
    const filteredMembers = nextCrew.members.filter(
      (member) =>
        !crewMembershipMap.has(member.memberId) || crewMembershipMap.get(member.memberId) === crewId
    );
    const nextCrewWithFilteredMembers = {
      ...nextCrew,
      members: filteredMembers,
    };
    const filteredPreferences = nextCrew.preferences.filter((boatId) => {
      const boat = boatsById.get(boatId);
      return crewCanBookBoat(boat, nextCrewWithFilteredMembers, membersById, availableBoatIds);
    });
    setCrews((prev) =>
      prev.map((crew) =>
        crew.id === crewId
          ? {
              ...crew,
              label: nextCrew.label,
              notes: nextCrew.notes,
              members: filteredMembers,
              preferences: filteredPreferences,
            }
          : crew
      )
    );
    const skippedMembers = nextCrew.members.length - filteredMembers.length;
    const skippedBoats = nextCrew.preferences.length - filteredPreferences.length;
    const skippedSummary = [
      skippedMembers > 0 ? `${skippedMembers} already-assigned member${skippedMembers === 1 ? '' : 's'} skipped` : '',
      skippedBoats > 0 ? `${skippedBoats} unavailable or incompatible boat${skippedBoats === 1 ? '' : 's'} skipped` : '',
    ]
      .filter(Boolean)
      .join(' • ');
    setNotice(
      `Applied preset "${preset.name || 'Preset'}" to ${
        crews.find((crew) => crew.id === crewId)?.label || 'crew'
      }${skippedSummary ? ` (${skippedSummary})` : ''}.`
    );
  };

  const deletePresetById = async (presetId, presetName = 'Preset') => {
    if (!teamId || !user?.uid || !presetId) return;
    const confirmed =
      typeof window !== 'undefined'
        ? window.confirm(`Delete preset "${presetName || 'Preset'}"?`)
        : false;
    if (!confirmed) return;
    await runPresetAction(
      async () => {
        await deleteDoc(doc(db, 'users', user.uid, 'outingCrewPresets', presetId));
        await refreshPresets('');
        setNotice(`Deleted preset "${presetName || 'Preset'}".`);
      },
      'Failed to delete the preset.'
    );
  };

  const deleteSelectedPreset = async () => {
    if (!selectedPreset?.id) return;
    await deletePresetById(selectedPreset.id, selectedPreset.name || 'Preset');
  };

  const renameSelectedPreset = async () => {
    if (!teamId || !user?.uid || !selectedPreset?.id) return;
    const nextName = presetManagerName.trim();
    if (!nextName) return;
    await runPresetAction(
      async () => {
        await updateDoc(doc(db, 'users', user.uid, 'outingCrewPresets', selectedPreset.id), {
          name: nextName,
          updatedAt: serverTimestamp(),
        });
        await refreshPresets(selectedPreset.id);
        setNotice(`Renamed preset to "${nextName}".`);
      },
      'Failed to rename the preset.'
    );
  };

  const createNewPresetDraft = () => {
    setSelectedPresetId('');
    setPresetManagerName('');
    setPresetDraft(emptyPresetDraft());
    setPresetMemberSearch('');
    setPresetMemberPicker('');
    setPresetBoatSearch('');
    setShowAllPresetBoats(false);
  };

  const selectPresetForCrew = (crewId, presetId) => {
    setPresetPickerByCrew((prev) => ({ ...prev, [crewId]: presetId }));
  };

  const presetAssignedMemberIds = useMemo(
    () =>
      new Set(
        (Array.isArray(presetDraft?.members) ? presetDraft.members : [])
          .map((member) => (typeof member?.memberId === 'string' ? member.memberId.trim() : ''))
          .filter(Boolean)
      ),
    [presetDraft]
  );

  const presetAvailableMembers = useMemo(() => {
    const search = presetMemberSearch.trim().toLowerCase();
    return memberOptions.filter((member) => {
      if (presetAssignedMemberIds.has(member.id)) return false;
      if (!search) return true;
      return member.name.toLowerCase().includes(search);
    });
  }, [memberOptions, presetAssignedMemberIds, presetMemberSearch]);

  const presetOrderedBoatChoices = useMemo(() => {
    const search = presetBoatSearch.trim().toLowerCase();
    return boats
      .filter((boat) => String(boat?.status || 'active').toLowerCase() !== 'maintenance')
      .map((boat) => ({
        boat,
        meta: boatPreferenceMeta(boat, presetDraft, membersById),
      }))
      .filter(({ boat }) => {
        if (!search) return true;
        const haystack = [
          boat?.name || '',
          boat?.type || '',
          ...(Array.isArray(boat?.tagsAllowed) ? boat.tagsAllowed : []),
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(search);
      })
      .sort((left, right) => {
        if (left.meta.recommended !== right.meta.recommended) {
          return left.meta.recommended ? -1 : 1;
        }
        if (left.meta.spareSeats !== right.meta.spareSeats) {
          return left.meta.spareSeats - right.meta.spareSeats;
        }
        return (left.boat?.name || '').localeCompare(right.boat?.name || '', undefined, {
          sensitivity: 'base',
        });
      });
  }, [boats, membersById, presetBoatSearch, presetDraft]);

  const presetVisibleBoatChoices = useMemo(() => {
    return presetOrderedBoatChoices.filter(({ boat, meta }) =>
      presetBoatSearch.trim() ||
      showAllPresetBoats ||
      meta.recommended ||
      presetDraft.preferences.includes(boat.id)
    );
  }, [presetBoatSearch, presetDraft.preferences, presetOrderedBoatChoices, showAllPresetBoats]);

  const presetHiddenBoatCount = useMemo(
    () => Math.max(0, presetOrderedBoatChoices.length - presetVisibleBoatChoices.length),
    [presetOrderedBoatChoices.length, presetVisibleBoatChoices.length]
  );

  const addPresetMember = () => {
    const memberId = presetMemberPicker;
    if (!memberId) return;
    const member = membersById.get(memberId);
    if (!member) return;
    setPresetDraft((prev) => ({
      ...prev,
      members: prev.members.some((entry) => entry.memberId === memberId)
        ? prev.members
        : [
            ...prev.members,
            {
              ...emptyCrewMember(),
              memberId,
              displayName: member.name,
            },
          ],
    }));
    setPresetMemberPicker('');
  };

  const removePresetMember = (memberId) => {
    setPresetDraft((prev) => ({
      ...prev,
      members: prev.members.filter((member) => member.memberId !== memberId),
    }));
  };

  const updatePresetMember = (memberId, patch) => {
    setPresetDraft((prev) => ({
      ...prev,
      members: prev.members.map((member) =>
        member.memberId === memberId ? { ...member, ...patch } : member
      ),
    }));
  };

  const togglePresetPreference = (boatId) => {
    setPresetDraft((prev) => {
      const next = new Set(prev.preferences || []);
      if (next.has(boatId)) next.delete(boatId);
      else next.add(boatId);
      return {
        ...prev,
        preferences: Array.from(next),
      };
    });
  };

  const movePresetPreference = (index, delta) => {
    setPresetDraft((prev) => {
      const next = [...(prev.preferences || [])];
      const targetIndex = index + delta;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return {
        ...prev,
        preferences: next,
      };
    });
  };

  const toggleCrewCollapsed = (crewId) => {
    setCollapsedCrews((prev) => ({ ...prev, [crewId]: !prev[crewId] }));
  };

  const setAllCrewsCollapsed = (collapsed) => {
    setCollapsedCrews(
      crews.reduce((acc, crew) => {
        acc[crew.id] = collapsed;
        return acc;
      }, {})
    );
  };

  const addCrewMember = (crewId, nextMemberId = '') => {
    const memberId = nextMemberId || memberPickerByCrew[crewId];
    if (!memberId) return;
    if (assignedMemberIds.has(memberId)) return;
    const memberData = eligibleMembersById.get(memberId) || membersById.get(memberId);
    if (!memberData) return;
    setCrews((prev) =>
      prev.map((crew) =>
        crew.id === crewId
          ? {
              ...crew,
              members: crew.members.some((member) => member.memberId === memberId)
                ? crew.members
                : [
                    ...crew.members,
                    {
                      ...emptyCrewMember(),
                      memberId,
                      displayName: memberData.name,
                    },
                  ],
            }
          : crew
      )
    );
    setMemberPickerByCrew((prev) => ({ ...prev, [crewId]: '' }));
    setMemberSearchByCrew((prev) => ({ ...prev, [crewId]: '' }));
  };

  const removeCrewMember = (crewId, memberId) => {
    setCrews((prev) =>
      prev.map((crew) =>
        crew.id === crewId
          ? { ...crew, members: crew.members.filter((member) => member.memberId !== memberId) }
          : crew
      )
    );
  };

  const updateCrewMember = (crewId, memberId, patch) => {
    setCrews((prev) =>
      prev.map((crew) =>
        crew.id === crewId
          ? {
              ...crew,
              members: crew.members.map((member) =>
                member.memberId === memberId ? { ...member, ...patch } : member
              ),
            }
          : crew
      )
    );
  };

  const toggleCrewPreference = (crewId, boatId) => {
    setCrews((prev) =>
      prev.map((crew) => {
        if (crew.id !== crewId) return crew;
        const next = new Set(crew.preferences || []);
        if (next.has(boatId)) next.delete(boatId);
        else next.add(boatId);
        return {
          ...crew,
          preferences: Array.from(next),
        };
      })
    );
  };

  const moveCrewPreference = (crewId, index, delta) => {
    setCrews((prev) =>
      prev.map((crew) => {
        if (crew.id !== crewId) return crew;
        const next = [...crew.preferences];
        const targetIndex = index + delta;
        if (targetIndex < 0 || targetIndex >= next.length) return crew;
        const [moved] = next.splice(index, 1);
        next.splice(targetIndex, 0, moved);
        return {
          ...crew,
          preferences: next,
        };
      })
    );
  };

  if (!isOpen) return null;

  const presetSaveButtonLabel = selectedPreset ? 'Save as new preset' : 'Save preset';

  const presetsSection = (
    <section
      ref={presetsRef}
      className="card"
      style={{
        padding: 16,
        border: highlightPresets ? '2px solid var(--team-primary, #2563eb)' : undefined,
        boxShadow: highlightPresets ? '0 0 0 4px color-mix(in srgb, var(--team-primary, #2563eb) 12%, transparent)' : undefined,
      }}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 6 }}>Crew Presets</h3>
          <p className="text-muted" style={{ margin: 0 }}>
            Manage your saved crew presets. Apply and save them from individual crews inside an outing.
          </p>
        </div>

        <label className="form-label">
          Saved presets
          <select
            className="input"
            value={selectedPresetId}
            onChange={(event) => setSelectedPresetId(event.target.value)}
          >
            <option value="">Select a preset</option>
            {presets.map((preset) => {
              const presetCrew = extractPresetCrew(preset);
              const memberCount = Array.isArray(presetCrew?.members) ? presetCrew.members.length : 0;
              return (
                <option key={preset.id} value={preset.id}>
                  {preset.name || 'Preset'} • {memberCount} rower{memberCount === 1 ? '' : 's'}
                </option>
              );
            })}
          </select>
        </label>

        <label className="form-label">
          Preset name
          <input
            className="input"
            value={presetManagerName}
            onChange={(event) => setPresetManagerName(event.target.value)}
            placeholder="Crew preset name"
          />
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            disabled={saving}
            onClick={createNewPresetDraft}
          >
            New preset
          </button>
          <button
            type="button"
            className="btn btn--primary btn--xs"
            disabled={saving || !presetManagerName.trim()}
            onClick={handleCreatePreset}
          >
            {presetSaveButtonLabel}
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            disabled={saving || !selectedPreset || !presetManagerName.trim()}
            onClick={handleUpdatePreset}
          >
            Update selected
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            disabled={saving || !selectedPreset || !presetManagerName.trim()}
            onClick={renameSelectedPreset}
          >
            Rename selected
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            disabled={saving || !selectedPreset}
            onClick={deleteSelectedPreset}
          >
            Delete selected
          </button>
        </div>

        <div className="card" style={{ padding: 12, background: '#f8fafc' }}>
          <div style={{ display: 'grid', gap: 10 }}>
            <label className="form-label">
              Crew label
              <input
                className="input"
                value={presetDraft.label || ''}
                onChange={(event) =>
                  setPresetDraft((prev) => ({ ...prev, label: event.target.value }))
                }
              />
            </label>

            <label className="form-label">
              Notes
              <textarea
                className="input"
                rows={2}
                value={presetDraft.notes || ''}
                onChange={(event) =>
                  setPresetDraft((prev) => ({ ...prev, notes: event.target.value }))
                }
              />
            </label>

            <div>
              <div className="form-label">Crew members</div>
              {presetDraft.members.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>No members selected yet.</p>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {presetDraft.members.map((member) => (
                    <div
                      key={`preset:${member.memberId}`}
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 8, alignItems: 'center' }}
                    >
                      <span>{member.displayName || membersById.get(member.memberId)?.name || 'Unknown member'}</span>
                      <input
                        className="input"
                        placeholder="Seat label"
                        value={member.seatLabel || ''}
                        onChange={(event) =>
                          updatePresetMember(member.memberId, { seatLabel: event.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => removePresetMember(member.memberId)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 8, marginTop: 10, alignItems: 'end' }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Search members
                  <input
                    className="input"
                    placeholder="Search members"
                    value={presetMemberSearch}
                    onChange={(event) => setPresetMemberSearch(event.target.value)}
                  />
                </label>
                <label className="form-label" style={{ margin: 0 }}>
                  Matching members
                  <select
                    className="input"
                    value={presetMemberPicker}
                    onChange={(event) => setPresetMemberPicker(event.target.value)}
                  >
                    <option value="">Select member</option>
                    {presetAvailableMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn btn--secondary btn--xs"
                  onClick={addPresetMember}
                >
                  Add member
                </button>
              </div>
            </div>

            <div>
              <div className="form-label">Boat preferences</div>
              <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: 8 }}>
                Showing the best-fitting boats for {presetDraft.members.length || 0} rower{presetDraft.members.length === 1 ? '' : 's'} first.
              </div>
              {presetDraft.preferences.length === 0 ? (
                <p className="text-muted" style={{ margin: '0 0 10px' }}>No boats selected yet.</p>
              ) : (
                <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                  {presetDraft.preferences.map((boatId, index) => {
                    const boat = boats.find((entry) => entry.id === boatId);
                    const meta = boatPreferenceMeta(boat || {}, presetDraft, membersById);
                    return (
                      <div
                        key={`preset-boat:${boatId}`}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}
                      >
                        <span>
                          {index + 1}. {boat?.name || boatId}
                          {boatMetaLabel(boat, meta) ? ` (${boatMetaLabel(boat, meta)})` : ''}
                        </span>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn--secondary btn--xs"
                            onClick={() => movePresetPreference(index, -1)}
                            disabled={index === 0}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="btn btn--secondary btn--xs"
                            onClick={() => movePresetPreference(index, 1)}
                            disabled={index === presetDraft.preferences.length - 1}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="btn btn--secondary btn--xs"
                            onClick={() => togglePresetPreference(boatId)}
                          >
                            Remove
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'grid', gap: 10 }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Search boats
                  <input
                    className="input"
                    placeholder="Search by boat name or type"
                    value={presetBoatSearch}
                    onChange={(event) => setPresetBoatSearch(event.target.value)}
                  />
                </label>

                {presetHiddenBoatCount > 0 && !presetBoatSearch.trim() && (
                  <div>
                    <button
                      type="button"
                      className="btn btn--secondary btn--xs"
                      onClick={() => setShowAllPresetBoats((prev) => !prev)}
                    >
                      {showAllPresetBoats ? 'Show best-fit boats only' : `Show all boats (${presetHiddenBoatCount} more)`}
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {presetVisibleBoatChoices.map(({ boat, meta }) => (
                    <label key={`preset-pick:${boat.id}`} className="tag-pill">
                      <input
                        type="checkbox"
                        checked={presetDraft.preferences.includes(boat.id)}
                        onChange={() => togglePresetPreference(boat.id)}
                      />
                      {boat.name || 'Boat'}
                      {boatMetaLabel(boat, meta) ? ` (${boatMetaLabel(boat, meta)})` : ''}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="btn btn--primary btn--xs"
                disabled={saving || !presetManagerName.trim()}
                onClick={handleCreatePreset}
              >
                {presetSaveButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  if (presetManagementOnly) {
    return (
      <div ref={modalScrollRef} className="modal-form outings-modal outings-modal--presets">
        <div className="outings-modal__header">
          <div>
            <h2 style={{ margin: 0 }}>Crew Presets</h2>
            <p className="text-muted outings-modal__header-copy">
              Build and maintain your regular crew setups here, then apply them inside an outing.
            </p>
          </div>
        </div>
        {error && <ErrorMessage message={error} />}

        <div className="outings-modal__preset-layout">
          {presetsSection}

          <section className="card outings-modal__panel" style={{ padding: 16 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <h3 style={{ margin: 0 }}>How presets work</h3>
              <p className="text-muted" style={{ margin: 0 }}>
                Presets are saved per user, per team, and are crew-based rather than outing-based.
              </p>
              <p className="text-muted" style={{ margin: 0 }}>
                Use this popup to create and manage preset crews. Apply them from inside an outing.
              </p>
            </div>
          </section>

          <div className="form-actions">
            <SubmitButton type="button" onClick={onClose} label="Close" variant="ghost" disabled={saving} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={modalScrollRef} className="modal-form outings-modal">
      <div className="outings-modal__header">
        <div>
          <h2 style={{ margin: 0 }}>Manage Outings</h2>
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--xs"
          onClick={() => setShowOutingsHelp((prev) => !prev)}
          aria-label="How outings work"
          title="How outings work"
        >
          ❓
        </button>
      </div>
      {error && <ErrorMessage message={error} />}
      <div className="outings-modal__callout outings-modal__callout--soft" style={{ marginBottom: 12 }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <strong>Build crews from an RSVP event and submit them for boat allocation.</strong>
          <div>Allocated crews are turned into real boat bookings.</div>
        </div>
      </div>
      {showOutingsHelp && (
        <div className="outings-modal__callout outings-modal__callout--soft" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <strong>How outings work</strong>
            <div>1. Link an RSVP event and pick the session date.</div>
            <div>2. Build crews from RSVP yes members and rank boat preferences for each crew.</div>
            <div>3. Submit the outing into shared allocation. Boats are preferences, not guaranteed bookings.</div>
            <div>4. If the slot is already live it may allocate immediately. Otherwise it waits for release day. Any crew with no boat can be reopened and adjusted.</div>
          </div>
        </div>
      )}

      {isMobileOutingsView && (
        <div className="outings-mobile-steps">
          {[
            { id: 'outings', label: 'Outings' },
            { id: 'details', label: 'Details' },
            { id: 'rsvps', label: 'RSVPs' },
            { id: 'crews', label: 'Crews' },
          ].map((step) => (
            <button
              key={step.id}
              type="button"
              className={mobileStep === step.id ? 'btn btn--secondary btn--xs' : 'btn btn--ghost btn--xs'}
              onClick={() => goToMobileStep(step.id)}
            >
              {step.label}
            </button>
          ))}
        </div>
      )}

      <div className="outings-modal__layout">
        {(!isMobileOutingsView || mobileStep === 'outings') && (
        <aside className="card outings-modal__sidebar">
          <div className="outings-modal__sidebar-head">
            <strong>Outings</strong>
            <button
              type="button"
              className="btn btn--secondary btn--xs"
              onClick={() => {
                setActiveKey('__new__');
                if (isMobileOutingsView) goToMobileStep('details');
              }}
            >
              New
            </button>
          </div>

          <div className="outings-modal__sidebar-summary">
            <span className="availability-pill unavailable">{summaryLabel(outingsSummary.draft, 'draft')}</span>
            <span className="availability-pill available">{summaryLabel(outingsSummary.submitted, 'submitted')}</span>
          </div>

          <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={outingListFilter === 'upcoming' ? 'btn btn--secondary btn--xs' : 'btn btn--ghost btn--xs'}
              onClick={() => setOutingListFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              type="button"
              className={outingListFilter === 'all' ? 'btn btn--secondary btn--xs' : 'btn btn--ghost btn--xs'}
              onClick={() => setOutingListFilter('all')}
            >
              All
            </button>
            <button
              type="button"
              className={outingListFilter === 'past' ? 'btn btn--secondary btn--xs' : 'btn btn--ghost btn--xs'}
              onClick={() => setOutingListFilter('past')}
            >
              Past
            </button>
          </div>

          {loading ? (
            <p className="text-muted">Loading…</p>
          ) : visibleOutings.length === 0 ? (
            <p className="text-muted">No outings yet.</p>
          ) : (
            <div className="outings-modal__sidebar-list">
              {visibleOutings.map((row) => (
                <div
                  key={row.id}
                  className={row.id === activeKey ? 'outings-modal__sidebar-item outings-modal__sidebar-item--active' : 'outings-modal__sidebar-item'}
                  style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setActiveKey(row.id);
                      if (isMobileOutingsView) goToMobileStep('details');
                    }}
                    style={{
                      border: 0,
                      background: 'transparent',
                      padding: 0,
                      textAlign: 'left',
                      display: 'grid',
                      gap: 4,
                    }}
                  >
                    <span style={{ textAlign: 'left' }}>
                      <strong>{row.name || row.linkedEventName || 'Outing'}</strong>
                      <span className="text-muted outings-modal__sidebar-date">
                        {formatOutingDate(row)}
                      </span>
                    </span>
                  </button>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="outings-modal__sidebar-icon">
                      {String(row.status || 'draft').toLowerCase() === 'submitted' ? '🚣' : '🧭'}
                    </span>
                    {String(row.status || 'draft').toLowerCase() === 'draft' && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--xs"
                        onClick={() => handleDeleteDraftOuting(row.id)}
                        disabled={saving}
                        title="Delete draft outing"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
        )}

        {(!isMobileOutingsView || mobileStep !== 'outings') && (
        <div className="outings-modal__main">
          {(!isMobileOutingsView || mobileStep === 'details') && (
          <section className="card outings-modal__panel" style={{ padding: 16 }}>
            <div className="outings-modal__panel-head">
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>
                  {outing.id ? 'Outing details' : 'New outing'}
                </h3>
                <p className="text-muted" style={{ margin: 0 }}>
                  Draft crews from an RSVP event and submit them into the shared boat allocation run.
                </p>
              </div>
              {activeOutingMeta && (
                <span className="availability-pill available">
                  {String(activeOutingMeta.status || 'draft').toLowerCase() === 'submitted' ? 'Submitted' : 'Draft'}
                </span>
              )}
            </div>

            <div className="outings-modal__details-grid">
              <label className="form-label">
                Outing name
                <input
                  className="input"
                  value={outing.name}
                  onChange={(event) => setOuting((prev) => ({ ...prev, name: event.target.value }))}
                  disabled={isSubmitted}
                />
              </label>

              <label className="form-label">
                Linked RSVP event
                <select
                  className="input"
                  value={outing.linkedEventId}
                  onChange={(event) => hydrateOutingFromEvent(event.target.value)}
                  disabled={isSubmitted}
                >
                  <option value="">Select an RSVP event</option>
                  {eventOptionGroups.recurring.length > 0 && (
                    <optgroup label="Recurring RSVP series">
                      {eventOptionGroups.recurring.map((eventData) => {
                        const usedDates = usedRecurringOccurrencesByEvent.get(eventData.id) || new Set();
                        const availableOccurrences = filterRecurringOccurrenceOptions(
                          buildRecurringOccurrenceOptions(
                            eventData,
                            outing.linkedEventId === eventData.id ? outing.dateIso : ''
                          ),
                          usedDates,
                          outing.linkedEventId === eventData.id ? outing.dateIso : ''
                        ).length;
                        return (
                          <option key={eventData.id} value={eventData.id}>
                            {formatEventPickerLabel(eventData, availableOccurrences)}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  {eventOptionGroups.oneOff.length > 0 && (
                    <optgroup label="One-off RSVP events">
                      {eventOptionGroups.oneOff.map((eventData) => (
                        <option key={eventData.id} value={eventData.id}>
                          {formatEventPickerLabel(eventData)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <span className="help-text">
                  Recurring series can be reused for different dates. One-off RSVP events can only be linked once.
                </span>
              </label>

              {selectedEvent && (
                <div className="text-muted outings-modal__callout outings-modal__callout--soft">
                  {selectedEventIsRecurring
                    ? `Recurring RSVP series • ${(selectedEvent.recurrence?.frequency || selectedEvent.frequency || 'Weekly').toLowerCase()} on ${
                        (Array.isArray(selectedEvent.recurrence?.byDay) ? selectedEvent.recurrence.byDay : [])
                          .map((dayCode) => WEEKDAY_LABELS[dayCode] || dayCode)
                          .join(', ') || 'scheduled days'
                      }`
                    : selectedEvent.date} • {(resolvedSelectedEventSchedule.fromTime || '').trim()} • {resolvedSelectedEventSchedule.durationMinutes || 60} mins
                </div>
              )}

              <label className="form-label">
                Outing date
                {selectedEventIsRecurring ? (
                  <select
                    className="input"
                    value={outing.dateIso}
                    onChange={(event) =>
                      setOuting((prev) => {
                        const nextDateIso = event.target.value;
                        const nextSchedule = resolveOutingSchedule(selectedEvent || {}, nextDateIso, eventOptions);
                        return {
                          ...prev,
                          dateIso: nextDateIso,
                          date: nextDateIso
                            ? Timestamp.fromDate(new Date(`${nextDateIso}T00:00:00`))
                            : null,
                          fromTime: nextSchedule.fromTime || '',
                          toTime: nextSchedule.toTime || '',
                          durationMinutes: nextSchedule.durationMinutes || 60,
                        };
                      })
                    }
                    disabled={isSubmitted}
                  >
                    <option value="">Select an occurrence</option>
                    {recurringOccurrenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    type="date"
                    value={outing.dateIso}
                    onChange={(event) => setOuting((prev) => ({
                      ...prev,
                      dateIso: event.target.value,
                      date: event.target.value ? Timestamp.fromDate(new Date(`${event.target.value}T00:00:00`)) : null,
                    }))}
                    disabled={isSubmitted || Boolean(selectedEvent?.date)}
                  />
                )}
                <span className="help-text">
                  {selectedEventIsRecurring
                    ? recurringOccurrenceOptions.length
                      ? `${recurringOccurrenceOptions.length} upcoming occurrence${recurringOccurrenceOptions.length === 1 ? '' : 's'} available for this series.`
                      : 'No unused upcoming occurrences are available for this recurring RSVP event.'
                    : 'Pulled from the linked event.'}
                </span>
              </label>

              <div className="text-muted outings-modal__callout outings-modal__callout--soft">
                RSVP yes pool: {eligibleMembers.length} member{eligibleMembers.length === 1 ? '' : 's'}
                {outing.tagFilters.length > 0
                  ? ` filtered by ${outing.tagFilters.length} tag${outing.tagFilters.length === 1 ? '' : 's'} in the member chooser`
                  : ''}
              </div>
              {notice && (
                <div className="text-muted outings-modal__callout outings-modal__callout--success">
                  {notice}
                </div>
              )}
            </div>
            {isMobileOutingsView && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--xs"
                  onClick={() => goToMobileStep('rsvps')}
                >
                  Continue to RSVPs
                </button>
              </div>
            )}
          </section>
          )}

          {(!isMobileOutingsView || mobileStep === 'rsvps') && (
          <section className="card outings-modal__panel" style={{ padding: 16 }}>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>Unallocated RSVPs</h3>
                <p className="text-muted" style={{ margin: 0 }}>
                  Members who have RSVP’d yes but are not currently assigned to any crew.
                </p>
              </div>

              {unallocatedRsvpMembers.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>
                  All RSVP yes members are allocated to a crew.
                </p>
              ) : (
                <>
                  <div className="text-muted" style={{ fontSize: '0.95rem' }}>
                    {unallocatedRsvpMembers.length} unallocated RSVP
                    {unallocatedRsvpMembers.length === 1 ? '' : 's'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {unallocatedRsvpMembers.map((member) => (
                      <span key={`unallocated:${member.id}`} className="tag-pill">
                        {member.name}
                        {outing.tagFilters.length > 0 && !eligibleMembersById.has(member.id) ? ' • outside filters' : ''}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
            {isMobileOutingsView && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--xs"
                  onClick={() => goToMobileStep('crews')}
                >
                  Continue to Crews
                </button>
              </div>
            )}
          </section>
          )}

          {(!isMobileOutingsView || mobileStep === 'crews') && (
          <section className="card outings-modal__panel" style={{ padding: 16 }}>
            <div className="outings-modal__panel-head">
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>Crews</h3>
                <p className="text-muted" style={{ margin: 0 }}>
                  Each crew becomes one grouped request in the shared allocator.
                </p>
              </div>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                {crews.length > 1 && (
                  <>
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => setAllCrewsCollapsed(true)}>
                      Collapse all
                    </button>
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => setAllCrewsCollapsed(false)}>
                      Expand all
                    </button>
                  </>
                )}
                {!isSubmitted && (
                  <button type="button" className="btn btn--secondary btn--xs" onClick={addCrew}>
                    Add crew
                  </button>
                )}
              </div>
            </div>

            {crews.length === 0 ? (
              <p className="text-muted" style={{ marginTop: 12 }}>
                No crews added yet.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                {crews.map((crew, index) => {
                  const isCollapsed = Boolean(collapsedCrews[crew.id]);
                  const availableMembers = eligibleMembers.filter((member) => !assignedMemberIds.has(member.id));
                  const crewSearch = (memberSearchByCrew[crew.id] || '').trim().toLowerCase();
                  const searchableMembers = crewSearch
                    ? availableMembers.filter((member) => member.name.toLowerCase().includes(crewSearch))
                    : availableMembers;
                  const selectedBoatLabel =
                    crew.selectedBoatId && boats.find((boat) => boat.id === crew.selectedBoatId)?.name;
                  const nonRsvpMembers = crew.members.filter((member) => !rsvpYesSet.has(member.memberId));
                  const liveAvailableBoatSet = availableBoatIds instanceof Set ? availableBoatIds : null;
                  const orderedBoatChoices = boats
                    .filter((boat) => String(boat?.status || 'active').toLowerCase() !== 'maintenance')
                    .map((boat) => ({
                      boat,
                      meta: {
                        ...boatPreferenceMeta(boat, crew, membersById),
                        availableNow: !liveAvailableBoatSet || liveAvailableBoatSet.has(boat.id),
                        },
                      }))
                      .sort((left, right) => {
                        if (left.meta.recommended !== right.meta.recommended) {
                          return left.meta.recommended ? -1 : 1;
                      }
                      if (left.meta.spareSeats !== right.meta.spareSeats) {
                        return left.meta.spareSeats - right.meta.spareSeats;
                      }
                      return (left.boat?.name || '').localeCompare(right.boat?.name || '', undefined, {
                          sensitivity: 'base',
                        });
                      });
                  const visibleBoatChoices = orderedBoatChoices.filter(({ meta }) => {
                    if (meta.availableNow === false) return false;
                    if (meta.fitsSeatCount === false) return false;
                    if (meta.tagCompatible === false) return false;
                    return true;
                  });
                  const hiddenTakenBoatCount = orderedBoatChoices.filter(({ meta }) => meta.availableNow === false).length;
                  const hiddenUnbookableBoatCount = orderedBoatChoices.filter(({ meta }) => (
                    meta.availableNow !== false && (meta.fitsSeatCount === false || meta.tagCompatible === false)
                  )).length;
                  const crewMemberNames = crew.members
                    .map((member) => member.displayName || membersById.get(member.memberId)?.name || 'Member')
                    .filter(Boolean);
                  const collapsedMemberSummary = crewMemberNames.length === 0
                    ? 'No rowers yet'
                    : crewMemberNames.length <= 3
                      ? crewMemberNames.join(', ')
                      : `${crewMemberNames.slice(0, 3).join(', ')} +${crewMemberNames.length - 3} more`;
                  const crewSummary = [
                    isCollapsed
                      ? collapsedMemberSummary
                      : `${crew.members.length} rower${crew.members.length === 1 ? '' : 's'}`,
                    `${crew.preferences.length} boat pref${crew.preferences.length === 1 ? '' : 's'}`,
                  ].join(' • ');
                  const crewReviewNotes = [
                    crew.members.length === 0 ? 'Add at least one rower.' : '',
                    crew.preferences.length === 0 ? 'Add at least one boat preference.' : '',
                    nonRsvpMembers.length > 0
                      ? `Waiting on RSVP yes from ${nonRsvpMembers
                          .map((member) => member.displayName || membersById.get(member.memberId)?.name || 'Member')
                          .join(', ')}.`
                      : '',
                  ].filter(Boolean);

                  return (
                    <div key={crew.id} className="card outings-crew-card">
                      <div className="outings-crew-card__header">
                        <div>
                          <strong>Crew {index + 1}</strong>
                          <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: 4 }}>
                            Status: {activeOutingIsPast && crew.status === 'unfilled' ? 'past outing' : (crew.status || 'draft')}
                            {crew.status === 'allocated' && selectedBoatLabel ? ` • ${selectedBoatLabel}` : ''}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: 2 }}>
                            {crew.label?.trim() ? `${crew.label.trim()} • ` : ''}{crewSummary}
                          </div>
                        </div>
                        <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn btn--secondary btn--xs"
                            onClick={() => toggleCrewCollapsed(crew.id)}
                          >
                            {isCollapsed ? 'Expand' : 'Collapse'}
                          </button>
                          {!isSubmitted && (
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                            <button type="button" className="btn btn--secondary btn--xs" onClick={() => removeCrew(crew.id)}>
                              Remove
                            </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="outings-crew-card__stats">
                        <span className="outings-crew-card__stat">{crew.members.length} member{crew.members.length === 1 ? '' : 's'}</span>
                        <span className="outings-crew-card__stat">{crew.preferences.length} boat pref{crew.preferences.length === 1 ? '' : 's'}</span>
                        <span className={`outings-crew-card__stat outings-crew-card__stat--${crewReviewNotes.length === 0 ? 'ready' : 'warning'}`}>
                          {crewReviewNotes.length === 0 ? 'Ready to allocate' : `${crewReviewNotes.length} item${crewReviewNotes.length === 1 ? '' : 's'} to fix`}
                        </span>
                      </div>

                      {!isCollapsed && (
                      <div className="outings-crew-card__body">
                        <section className="outings-crew-stage">
                          <div className="outings-crew-stage__head">
                            <div>
                              <div className="outings-crew-stage__eyebrow">Step 1</div>
                              <h4 className="outings-crew-stage__title">Crew setup</h4>
                            </div>
                            {!isSubmitted && (
                              <div className="outings-crew-stage__actions">
                                <label className="form-label" style={{ margin: 0, minWidth: '16rem' }}>
                                  Apply crew preset
                                  <select
                                    className="input"
                                    value={presetPickerByCrew[crew.id] || ''}
                                    onChange={(event) => {
                                      const presetId = event.target.value;
                                      selectPresetForCrew(crew.id, presetId);
                                      if (!presetId) return;
                                      applyPresetToCrew(crew.id, presetId);
                                    }}
                                  >
                                    <option value="">Select a crew preset</option>
                                    {presets.map((preset) => (
                                      <option key={preset.id} value={preset.id}>
                                        {preset.name || 'Preset'}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--xs"
                                  onClick={() => handleSaveCrewAsPreset(crew)}
                                  disabled={
                                    saving ||
                                    (!(crew.label || '').trim() &&
                                      crew.members.length === 0 &&
                                      crew.preferences.length === 0)
                                  }
                                >
                                  Save crew as preset
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="outings-crew-stage__content outings-crew-stage__content--two-col">
                            <label className="form-label">
                              Crew label
                              <input
                                className="input"
                                value={crew.label}
                                onChange={(event) => updateCrew(crew.id, { label: event.target.value })}
                                disabled={isSubmitted}
                              />
                            </label>

                            <label className="form-label">
                              Notes
                              <textarea
                                className="input"
                                value={crew.notes}
                                onChange={(event) => updateCrew(crew.id, { notes: event.target.value })}
                                disabled={isSubmitted}
                                rows={2}
                              />
                            </label>
                          </div>
                        </section>

                        <section className="outings-crew-stage">
                          <div className="outings-crew-stage__head">
                            <div>
                              <div className="outings-crew-stage__eyebrow">Step 2</div>
                              <h4 className="outings-crew-stage__title">Members</h4>
                            </div>
                            <span className="outings-crew-stage__summary">
                              {availableMembers.length} RSVP yes available
                            </span>
                          </div>
                          {crew.members.length === 0 ? (
                            <p className="text-muted">No members assigned yet.</p>
                          ) : (
                            <div className="outings-crew-members">
                              {crew.members.map((member) => (
                                <div
                                  key={`${crew.id}:${member.memberId}`}
                                  className="outings-crew-member-row"
                                >
                                  <span>{member.displayName || membersById.get(member.memberId)?.name || 'Unknown member'}</span>
                                  <input
                                    className="input"
                                    placeholder="Seat label"
                                    value={member.seatLabel}
                                    onChange={(event) =>
                                      updateCrewMember(crew.id, member.memberId, { seatLabel: event.target.value })
                                    }
                                    disabled={isSubmitted}
                                  />
                                  {!isSubmitted && (
                                    <button
                                      type="button"
                                      className="btn btn--secondary btn--xs"
                                      onClick={() => removeCrewMember(crew.id, member.memberId)}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {nonRsvpMembers.length > 0 && (
                            <div className="outings-crew-inline-warning">
                              Waiting on RSVP yes from {nonRsvpMembers.map((member) => member.displayName || membersById.get(member.memberId)?.name || 'Member').join(', ')}.
                            </div>
                          )}

                          {!isSubmitted && (
                            <div className="outings-crew-picker">
                              <div className="outings-crew-picker__head">
                                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                  RSVP yes picker • {availableMembers.length} available
                                </div>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--xs"
                                  onClick={() => setMemberChooser({ crewId: crew.id, search: '' })}
                                >
                                  Add from all members
                                </button>
                              </div>
                              <div className="outings-crew-picker__controls">
                                <label className="form-label" style={{ margin: 0 }}>
                                  Search RSVPs
                                  <input
                                    className="input"
                                    placeholder="Search RSVP yes members"
                                    value={memberSearchByCrew[crew.id] || ''}
                                    onChange={(event) =>
                                      setMemberSearchByCrew((prev) => ({ ...prev, [crew.id]: event.target.value }))
                                    }
                                  />
                                </label>
                                <label className="form-label" style={{ margin: 0 }}>
                                  Matching RSVPs
                                  <select
                                    className="input"
                                    value={memberPickerByCrew[crew.id] || ''}
                                    onChange={(event) => {
                                      const memberId = event.target.value;
                                      setMemberPickerByCrew((prev) => ({ ...prev, [crew.id]: memberId }));
                                      if (!memberId) return;
                                      addCrewMember(crew.id, memberId);
                                    }}
                                  >
                                    <option value="">Select RSVP member</option>
                                    {searchableMembers.map((member) => (
                                      <option key={member.id} value={member.id}>
                                        {member.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                Pick a rower from the RSVP list to add them straight into this crew.
                              </div>
                            </div>
                          )}
                        </section>

                        <section className="outings-crew-stage">
                          <div className="outings-crew-stage__head">
                            <div>
                              <div className="outings-crew-stage__eyebrow">Step 3</div>
                              <h4 className="outings-crew-stage__title">Boats</h4>
                            </div>
                            <span className="outings-crew-stage__summary">
                              {crew.preferences.length} ranked
                            </span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: 8 }}>
                            {checkingBoatAvailability
                              ? 'Checking which boats are already booked for this slot…'
                              : `Showing boats this crew can use and that are free for ${outing.fromTime || 'this slot'}${hiddenTakenBoatCount > 0 ? ` (${hiddenTakenBoatCount} taken hidden)` : ''}${hiddenUnbookableBoatCount > 0 ? `${hiddenTakenBoatCount > 0 ? ' • ' : ' ('}${hiddenUnbookableBoatCount} not bookable for this crew hidden${hiddenTakenBoatCount > 0 ? '' : ')'}` : hiddenTakenBoatCount > 0 ? '' : '.'}`}
                          </div>
                          {boatAvailabilityError && (
                            <div className="outings-crew-inline-warning" style={{ marginBottom: 8 }}>
                              {boatAvailabilityError}
                            </div>
                          )}
                          {crew.preferences.length === 0 ? (
                            <p className="text-muted">No boats selected yet.</p>
                          ) : (
                            <div className="outings-crew-boat-list">
                              {crew.preferences.map((boatId, prefIndex) => {
                                const boat = boats.find((entry) => entry.id === boatId);
                                const meta = {
                                  ...boatPreferenceMeta(boat || {}, crew, membersById),
                                  availableNow: !liveAvailableBoatSet || liveAvailableBoatSet.has(boatId),
                                };
                                return (
                                  <div
                                    key={`${crew.id}:${boatId}`}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
                                  >
                                    <span>
                                      {prefIndex + 1}. {boat?.name || boatId}
                                      {boatMetaWithAvailabilityLabel(boat, meta) ? ` (${boatMetaWithAvailabilityLabel(boat, meta)})` : ''}
                                    </span>
                                    {!isSubmitted && (
                                      <span style={{ display: 'inline-flex', gap: 6 }}>
                                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => moveCrewPreference(crew.id, prefIndex, -1)} disabled={prefIndex === 0}>
                                          ↑
                                        </button>
                                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => moveCrewPreference(crew.id, prefIndex, 1)} disabled={prefIndex === crew.preferences.length - 1}>
                                          ↓
                                        </button>
                                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => toggleCrewPreference(crew.id, boatId)}>
                                          Remove
                                        </button>
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!isSubmitted && (
                            <div className="outings-crew-boat-picker">
                              <div className="outings-crew-boat-chip-grid">
                              {visibleBoatChoices.map(({ boat, meta }) => (
                                  <label key={`${crew.id}:boat:${boat.id}`} className="tag-pill">
                                    <input
                                      type="checkbox"
                                      checked={crew.preferences.includes(boat.id)}
                                      onChange={() => toggleCrewPreference(crew.id, boat.id)}
                                    />
                                    {boat.name || 'Boat'}{boatMetaWithAvailabilityLabel(boat, meta) ? ` (${boatMetaWithAvailabilityLabel(boat, meta)})` : ''}
                                  </label>
                                ))}
                              </div>
                              {!checkingBoatAvailability && visibleBoatChoices.length === 0 && (
                                <p className="text-muted" style={{ margin: 0 }}>
                                  No boats are currently free for this crew in this time slot.
                                </p>
                              )}
                            </div>
                          )}
                        </section>

                        <section className="outings-crew-stage outings-crew-stage--review">
                          <div className="outings-crew-stage__head">
                            <div>
                              <div className="outings-crew-stage__eyebrow">Step 4</div>
                              <h4 className="outings-crew-stage__title">Review</h4>
                            </div>
                          </div>
                          {crewReviewNotes.length === 0 ? (
                            <div className="outings-crew-review-note outings-crew-review-note--ok">
                              This crew is ready for allocation.
                            </div>
                          ) : (
                            <div className="outings-crew-review-list">
                              {crewReviewNotes.map((note) => (
                                <div key={note} className="outings-crew-review-note outings-crew-review-note--warn">
                                  {note}
                                </div>
                              ))}
                            </div>
                          )}
                        </section>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          )}

          <div className="form-actions outings-modal__actions">
            <SubmitButton type="button" onClick={onClose} label="Close" variant="ghost" disabled={saving} />
            {isSubmitted ? (
              <>
                {(!activeOutingIsPast && (crewStatusSummary.unfilled > 0 || crewStatusSummary.allocated > 0)) && (
                  <div className="text-muted" style={{ marginRight: 'auto', fontSize: '0.95rem' }}>
                    {crewStatusSummary.unfilled > 0
                      ? `${crewStatusSummary.unfilled} crew${crewStatusSummary.unfilled === 1 ? '' : 's'} had no boat. Reassign to edit crews or boat preferences, then submit again.`
                      : 'Reassign to return this outing to draft, adjust crews or boat preferences, then submit again.'}
                  </div>
                )}
                <SubmitButton
                  type="button"
                  onClick={handleReopen}
                  label={saving ? 'Reassigning…' : 'Reassign Outing'}
                  variant="secondary"
                  disabled={saving || !outing.id}
                />
              </>
            ) : (
              <>
                <SubmitButton
                  type="button"
                  onClick={handleSaveDraft}
                  label={saving ? 'Saving…' : 'Save Draft'}
                  variant="secondary"
                  disabled={saving}
                />
                <SubmitButton
                  type="button"
                  onClick={handleSubmit}
                  label={saving ? 'Submitting…' : 'Submit To Allocation'}
                  variant="primary"
                  disabled={saving || !outing.linkedEventId || (selectedEventIsRecurring && !outing.dateIso)}
                />
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {memberChooser && (
        <div className="modal-backdrop" style={{ zIndex: 1200, background: 'rgba(17, 24, 39, 0.45)' }} onClick={() => setMemberChooser(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: '42rem', width: 'min(42rem, calc(100vw - 2rem))' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                <div>
                  <h3 style={{ margin: 0 }}>Add from all members</h3>
                  <p className="text-muted" style={{ margin: '0.35rem 0 0' }}>
                    Search the whole team, then add people into this crew. Members still need an RSVP yes before submission.
                  </p>
                </div>
                <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMemberChooser(null)}>
                  Close
                </button>
              </div>

              <label className="form-label" style={{ margin: 0 }}>
                Search members
                <input
                  className="input"
                  placeholder="Search by display name"
                  value={memberChooser.search || ''}
                  onChange={(event) => setMemberChooser((prev) => ({ ...(prev || {}), search: event.target.value }))}
                />
              </label>

              <div>
                <div className="form-label">Tag filters</div>
                {teamTagNames.length === 0 ? (
                  <p className="text-muted" style={{ margin: 0 }}>No team tags available.</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {teamTagNames.map((tagName) => {
                      const selected = outing.tagFilters.includes(tagName);
                      return (
                        <label key={tagName} className="tag-pill" style={{ opacity: isSubmitted ? 0.75 : 1 }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={isSubmitted}
                            onChange={() =>
                              setOuting((prev) => ({
                                ...prev,
                                tagFilters: selected
                                  ? prev.tagFilters.filter((name) => name !== tagName)
                                  : [...prev.tagFilters, tagName],
                              }))
                            }
                          />
                          {tagName}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gap: 8, maxHeight: '22rem', overflow: 'auto' }}>
                {memberOptions
                  .filter((member) => {
                    if (assignedMemberIds.has(member.id)) return false;
                    if (outing.tagFilters.length > 0 && !outing.tagFilters.some((tagName) => member.tagsSet.has(tagName))) {
                      return false;
                    }
                    if (memberChooser?.search) {
                      return member.name.toLowerCase().includes(memberChooser.search.trim().toLowerCase());
                    }
                    return true;
                  })
                  .map((member) => {
                    const activeCrew = crews.find((crew) => crew.id === memberChooser.crewId);
                    const alreadyInCrew = activeCrew?.members.some((crewMember) => crewMember.memberId === member.id);
                    return (
                      <div
                        key={`all-member:${member.id}`}
                        className="card"
                        style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
                      >
                        <div>
                          <strong>{member.name}</strong>
                          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {rsvpYesSet.has(member.id) ? 'RSVP yes' : 'No RSVP yes yet'}
                            {outing.tagFilters.length > 0 ? ` • matching ${outing.tagFilters.length} tag filter${outing.tagFilters.length === 1 ? '' : 's'}` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn--secondary btn--xs"
                          disabled={alreadyInCrew}
                          onClick={() => addCrewMember(memberChooser.crewId, member.id)}
                        >
                          {alreadyInCrew ? 'Added' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                {memberOptions.filter((member) => {
                  if (assignedMemberIds.has(member.id)) return false;
                  if (outing.tagFilters.length > 0 && !outing.tagFilters.some((tagName) => member.tagsSet.has(tagName))) {
                    return false;
                  }
                  if (memberChooser?.search) {
                    return member.name.toLowerCase().includes(memberChooser.search.trim().toLowerCase());
                  }
                  return true;
                }).length === 0 && (
                  <p className="text-muted" style={{ margin: 0 }}>
                    No members match the current search and tag filters.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
